import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { FieldDefinition, Sort } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import {
  LoadFailed,
  NoMatches,
  NothingYet,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { toggleSort } from "@/components/table/sorting";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  projectInvoice,
  projectUninvoicedOrder,
} from "@/modules/gst/projections";
import { InvoiceDetail } from "@/modules/gst/screens/InvoiceDetail";
import type { SectionScreenProps } from "@/modules/types";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { useOpenRecord } from "@/routes/useOpenRecord";

/**
 * Columns for the Uninvoiced Orders table.
 *
 * Local to this screen rather than in `fields.ts`: those declarations feed
 * the shared filter builder and column chooser for `INVOICE_FIELDS`, and
 * this table deliberately does not run on either — see the comment on
 * `uninvoicedQuery` below.
 */
const UNINVOICED_ORDER_FIELDS: FieldDefinition[] = [
  { name: "orderNumber", label: "Order number", type: "text", sortable: true },
  { name: "placedAt", label: "Placed", type: "date", sortable: true },
  { name: "customerName", label: "Customer", type: "text" },
  { name: "shippingProvinceCode", label: "Ship-to state", type: "text" },
  { name: "total", label: "Total", type: "money", sortable: true },
];

/**
 * The Invoices list (ticket #89), a toggle onto Uninvoiced Orders
 * (FR-GST-18), and — in place of both once a row is clicked — the invoice
 * detail (ticket #90).
 *
 * The detail is not a route: `config/modules.ts` names no
 * `gst.invoices.detail` section, so there is nothing for the router to match
 * against a `:id`. Keeping it as a local view swapped in here, rather than a
 * separate section, is what lets a click open it without a change to the
 * shared navigation map.
 */
export function InvoiceList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const { recordId, open } = useOpenRecord();
  const [view, setView] = useState<"invoices" | "uninvoiced">("invoices");

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const invoicesQuery = useQuery({
    queryKey: ["gst", "invoices", filter, search, sort, limit, cursor],
    queryFn: () => data.invoicing.list({ filter, search, sort, limit, cursor }),
  });

  const [uninvoicedSort, setUninvoicedSort] = useState<Sort[]>([]);
  const [uninvoicedLimit, setUninvoicedLimit] = useState(25);
  // Its sort is this screen's own state, not the URL's (#458).
  const [uninvoicedHistory, setUninvoicedHistory] =
    useCursorHistory(uninvoicedSort);
  const uninvoicedCursor = uninvoicedHistory[uninvoicedHistory.length - 1];

  /**
   * Its own query, unfiltered by the invoice filter the toolbar builds.
   *
   * `filter` above is built against `INVOICE_FIELDS` — an `Order` row has no
   * `invoiceNumber` or `buyerName`, and the fixture adapter's predicate
   * treats an unknown field as absent, which would make most operators
   * silently empty this table the moment someone touched a condition
   * intended for the invoice list. Always fetched (not gated behind the
   * toggle) so the count on the tab is accurate before anyone switches to it
   * — the whole point of FR-GST-18 is that this number gets noticed.
   */
  const uninvoicedQuery = useQuery({
    queryKey: [
      "gst",
      "uninvoiced-orders",
      uninvoicedSort,
      uninvoicedLimit,
      uninvoicedCursor,
    ],
    queryFn: () =>
      data.invoicing.listUninvoicedOrders({
        sort: uninvoicedSort,
        limit: uninvoicedLimit,
        cursor: uninvoicedCursor,
      }),
  });

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {recordId !== null ? (
        <RecordDrawer
          title={
            invoicesQuery.data?.items.find((invoice) => invoice.id === recordId)
              ?.invoiceNumber ?? "Invoice"
          }
        >
          <InvoiceDetail invoiceId={recordId} />
        </RecordDrawer>
      ) : null}
      <div className="flex shrink-0 items-center gap-1 border-b px-1 pb-2">
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            view === "invoices" && "bg-accent text-accent-foreground",
          )}
          onClick={() => {
            setView("invoices");
          }}
        >
          Invoices
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            view === "uninvoiced" && "bg-accent text-accent-foreground",
          )}
          onClick={() => {
            setView("uninvoiced");
          }}
        >
          Uninvoiced orders
          {uninvoicedQuery.data
            ? ` (${uninvoicedQuery.data.total ?? uninvoicedQuery.data.items.length})`
            : ""}
        </Button>
      </div>

      {view === "invoices" ? (
        <>
          {invoicesQuery.isPending ? (
            <TableSkeleton />
          ) : invoicesQuery.isError ? (
            <LoadFailed
              message={invoicesQuery.error.message}
              onRetry={() => {
                void invoicesQuery.refetch();
              }}
            />
          ) : (
            <DataTable
              fields={fields}
              rows={invoicesQuery.data.items}
              getRowId={(invoice) => invoice.id}
              project={projectInvoice}
              sort={sort}
              onToggleSort={onToggleSort}
              onRowClick={(invoice) => {
                open(invoice.id);
              }}
              emptyState={<NoMatches onClear={onClearFilter} />}
              footer={
                <Pagination
                  page={invoicesQuery.data}
                  limit={limit}
                  onLimitChange={(next) => {
                    setLimit(next);
                    setHistory([undefined]);
                  }}
                  history={history}
                  onNavigate={setHistory}
                />
              }
            />
          )}
        </>
      ) : uninvoicedQuery.isPending ? (
        <TableSkeleton />
      ) : uninvoicedQuery.isError ? (
        <LoadFailed
          message={uninvoicedQuery.error.message}
          onRetry={() => {
            void uninvoicedQuery.refetch();
          }}
        />
      ) : (
        <DataTable
          fields={UNINVOICED_ORDER_FIELDS}
          rows={uninvoicedQuery.data.items}
          getRowId={(order) => order.id}
          project={projectUninvoicedOrder}
          sort={uninvoicedSort}
          onToggleSort={(field, additive) => {
            setUninvoicedSort((current) =>
              toggleSort(current, field, additive),
            );
          }}
          emptyState={
            <NothingYet
              title="Nothing uninvoiced"
              description="Every fulfilled order that can carry an invoice has one."
            />
          }
          footer={
            <Pagination
              page={uninvoicedQuery.data}
              limit={uninvoicedLimit}
              onLimitChange={(next) => {
                setUninvoicedLimit(next);
                setUninvoicedHistory([undefined]);
              }}
              history={uninvoicedHistory}
              onNavigate={setUninvoicedHistory}
            />
          }
        />
      )}
    </div>
  );
}
